import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { bindActionCreators } from 'redux';
import ModalV2 from '/@/components/bayer/modal-v2';
import Drawing from '/@/components/drawing';
import Popup from '/@/components/popup';
import RecommendationCreate from '/@/components/recommendation-create';
import FieldSelect from '/@/components/recommendation-create/bayer/fields-step';
import settings from '/@/core/settings';
import { getAndSetCropMonitoringOnNitrogenSmartRedux } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useFieldById } from '/@/hooks/fields';
import { useNSRecommendationById } from '/@/hooks/nitrogen-smart';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import { setField } from '/@/store/crop-monitoring/actions';
import { getField } from '/@/store/crop-monitoring/selectors';
import { removeAll as removeAllAction, setNitrogenRecommendation } from '/@/store/nitrogen-recommendation/actions';
import {
    createNSRecommendationFromAPI,
    updateNSRecommendationFromAPI,
} from '/@/store/nitrogen-recommendation/apiProviders';
import {
    getApplicationDate,
    getManagementZoneFile,
    getNitrogenRecommendationsData,
    getPhone,
    getType,
    isSiembraStepValid,
    isValidForm,
} from '/@/store/nitrogen-recommendation/selectors';
import { axFetch } from '/@/store/session/actions';
import { getAppInstance } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import type { Step } from '/@/types/recommendation-create';
import { validatePhoneNumber } from '/@/utils/phone-numbers';
import { reverse } from '/@/utils/router';
import ContactStepper from './components/contact-stepper';
import FieldStepper from './components/field-stepper';

const NitrogenRecommendationForm = ({ fieldId }) => {
    const dispatch = useDispatch();
    const history = useNavigate();
    const selectedFieldId = useSelector(getField);
    const { data: preselectedField } = useFieldById({ id: fieldId }, { enabled: !!fieldId });
    const [activeStep, setActiveStep] = useState(0);
    const [selectedField, setSelectedField] = useState<Field | undefined>(undefined);
    const [error, setError] = useState<boolean>();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const setFieldDispatch = bindActionCreators(setField, dispatch);
    const removeAll = bindActionCreators(removeAllAction, dispatch);
    const managementZoneFile = useSelector(getManagementZoneFile);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const appInstance = useSelector(getAppInstance);
    const nitrogenRecommendationData = useSelector(getNitrogenRecommendationsData);
    const type = useSelector(getType);
    const applicationDate = useSelector(getApplicationDate);
    const phone = useSelector(getPhone);
    const qparams = new URLSearchParams(window.location.search);
    const { data: nitrogenRecommendationDataEdit } = useNSRecommendationById(qparams.get('id'));
    const isBayerUser = useIsValidatedBayerUserType();

    const isEdit = Boolean(qparams.get('id'));
    const nitrogenRecommendationDataEditHasLoaded = !isEdit || (nitrogenRecommendationDataEdit && isEdit);

    const cropMonitoringId = qparams.get('cropMonitoringId');

    const siembraValid = useSelector(isSiembraStepValid);
    const valid = useSelector((state) => isValidForm(state, type));
    // Phone must be present and complete to finish — auto-filled from the grower, but the
    // user has to complete it when the grower's number is missing or partial.
    const isPhoneValid = !!phone && validatePhoneNumber(phone);

    const handleReset = () => {
        setActiveStep(0);
        removeAll();
        setSelectedField(undefined);
        setFieldDispatch(undefined);
    };

    // Reset on mount.
    // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount
    useEffect(() => {
        handleReset();
    }, []);

    // Preselected field via URL param: select it and skip the field-selection step.
    // biome-ignore lint/correctness/useExhaustiveDependencies: setFieldDispatch is stable
    useEffect(() => {
        if (preselectedField) {
            setSelectedField(preselectedField);
            setFieldDispatch(preselectedField.id);
            setActiveStep((prev) => (prev === 0 ? 1 : prev));
        }
    }, [preselectedField]);

    // Crop-monitoring prefill: hydrate redux and jump to "Información de la siembra".
    // biome-ignore lint/correctness/useExhaustiveDependencies: dispatch is stable
    useEffect(() => {
        if (!cropMonitoringId) return;
        const fetch = async () => {
            await getAndSetCropMonitoringOnNitrogenSmartRedux(dispatch, cropMonitoringId);
            setActiveStep(1);
        };
        fetch();
    }, [cropMonitoringId]);

    // Edit flow: hydrate redux from the existing recommendation and jump to the requested step.
    // biome-ignore lint/correctness/useExhaustiveDependencies: hydrate only when edit data loads
    useEffect(() => {
        removeAll();
        if (isEdit && nitrogenRecommendationDataEdit) {
            dispatch(setNitrogenRecommendation(nitrogenRecommendationDataEdit || {}));

            // Editing always starts past field selection — the field is fixed for an
            // existing recommendation. Honor an explicit ?step when provided, otherwise
            // land on "Información de la siembra".
            setFieldDispatch(nitrogenRecommendationDataEdit?.field);
            const requestedStep = Number(qparams.get('step')) - 1;
            setActiveStep(requestedStep > 0 ? requestedStep : 1);
        }
    }, [nitrogenRecommendationDataEdit]);

    const handleSelectField = (field?: Field) => {
        setSelectedField(field);
        setFieldDispatch(field?.id);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            let managementZoneFiles = [];
            if (type === 'variable') {
                const data = new FormData();
                Array.from(managementZoneFile).forEach((mzf) => {
                    if (!mzf.id) data.append('files', mzf);
                    else {
                        managementZoneFiles.push(mzf); // Agrego los que se mantienen
                    }
                });
                if (data.get('files')) {
                    const response = await axFetch.post(settings.urls.managementZonesFile.upload, data);
                    if (response.status !== 201) {
                        setError(true);
                        setIsSubmitting(false);
                        return;
                    }
                    managementZoneFiles = managementZoneFiles.concat(response.data);
                }
            }

            const createdId = await handleCreateNitrogenRecommendation(managementZoneFiles);
            if (!createdId) return;

            handleReset();
            setShowDisclaimer(true);
            return createdId;
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreateNitrogenRecommendation = async (managementZoneFiles = []) => {
        try {
            const data = {
                ...nitrogenRecommendationData,
                managementZoneFiles,
                managementZoneFilesIds: managementZoneFiles ? managementZoneFiles.map((e) => e.id) : null,
                phone,
                applicationDate,
                // 'Tipo de producción' was removed from the form; let the backend apply its
                // default. Distribuidor was removed too — keep sending it empty.
                distributor: '',
                type,
                field: selectedFieldId,
                status: settings.choices.nitrogenRecommendationStatus?.[0]?.value,
            };
            delete data.technique;

            data.cropMonitoring = nitrogenRecommendationData.cropMonitoring?.id || undefined;

            if (isEdit) {
                await updateNSRecommendationFromAPI(data);
                setError(false);
                return qparams.get('id');
            }

            const res = await createNSRecommendationFromAPI({ ...data, appInstance: appInstance.id });
            setError(false);
            return res.id;
        } catch (err) {
            console.error(err);
            setError(true);
        }
    };

    const steps: Step[] = [
        {
            sidebarName: _('Selección del lote'),
            recommendationType: _('Recomendación N-Smart'),
            title: _('Elegí el lote para generar la recomendación'),
            subtitle: _('Seleccioná el lote para obtener una recomendación ajustada a sus condiciones específicas.'),
            content: <FieldSelect field={selectedField} setField={handleSelectField} polygonFilter growersOnly />,
            onClickNext: () => setActiveStep(1),
            onClickNextDisabled: () => !selectedFieldId,
            onClickBackDisabled: () => true,
        },
        {
            sidebarName: _('Información de la siembra'),
            recommendationType: _('Recomendación N-Smart'),
            title: _('Información de la siembra'),
            subtitle: _('Completá los datos de la siembra y elegí cómo querés la refertilización.'),
            content: <FieldStepper section='siembra' selected={selectedFieldId} edit={isEdit} />,
            onClickNext: () => setActiveStep(2),
            onClickNextDisabled: () => !siembraValid,
        },
        {
            sidebarName: _('Ambiente'),
            recommendationType: _('Recomendación N-Smart'),
            title:
                type === 'fixed'
                    ? _('Configuración de ambiente - Dosis fija')
                    : _('Configuración de ambiente - Dosis variable'),
            subtitle: _(
                'Analizamos el estado del cultivo con imágenes satelitales para ajustar la refertilización del maíz.'
            ),
            content: <FieldStepper section='ambiente' selected={selectedFieldId} edit={isEdit} />,
            onClickNext: () => setActiveStep(3),
            onClickNextDisabled: () => !valid,
        },
        {
            sidebarName: _('Contacto'),
            recommendationType: _('Recomendación N-Smart'),
            title: _('Contacto del productor'),
            subtitle: _(
                'Si tenemos alguna duda sobre la recomendación, usamos estos datos para contactar al productor.'
            ),
            content: <ContactStepper />,
            onClickNext: () => {
                mpTrackEvent('N-Smart Recommendation saved', { entry_source: 'nitrogen-recommendation-detail' });
                handleSubmit();
            },
            onClickNextText: _('Finalizar'),
            onClickNextDisabled: () => !valid || !isPhoneValid,
        },
    ];

    return (
        <React.Fragment>
            {RecommendationCreate({
                steps,
                activeStep,
                setActiveStep,
                isLoading: !nitrogenRecommendationDataEditHasLoaded,
                areActionsLoading: isSubmitting,
            })}

            {error && (
                <Popup visible={error} borderRadius showCloseIcon onClose={() => setError(false)}>
                    <Grid container direction='column' sx={{ gap: 2, p: 2 }}>
                        <Grid item>
                            <Drawing
                                size='sm'
                                type='no-share-corn'
                                title={<span style={{ fontWeight: 800 }}>{_('Importante')}</span>}
                                subtitle={_('Ha ocurrido un error, por favor inténtalo más tarde.')}
                            />
                        </Grid>
                        <Grid item>
                            <Button variant='contained' fullWidth onClick={() => setError(false)}>
                                {_('Continuar')}
                            </Button>
                        </Grid>
                    </Grid>
                </Popup>
            )}

            <ModalV2
                open={showDisclaimer}
                onClose={() => {
                    setShowDisclaimer(false);
                    history(reverse('Growers.list'));
                }}
                title={_('¡Listo, recibimos tu pedido!')}
                actionsProps={{ justifyContent: 'flex-end' }}
                actions={
                    <React.Fragment>
                        {isBayerUser ? (
                            <Button
                                variant='text'
                                color='secondary'
                                onClick={() => {
                                    setShowDisclaimer(false);
                                    history(reverse('Growers.list'));
                                }}
                            >
                                {_('Ir a Mis Productores')}
                            </Button>
                        ) : (
                            <Button
                                variant='text'
                                color='secondary'
                                onClick={() => {
                                    setShowDisclaimer(false);
                                    history(reverse('NewHome'));
                                }}
                            >
                                {_('Ir a la página principal')}
                            </Button>
                        )}
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={() => {
                                setShowDisclaimer(false);
                                handleReset();
                                history(reverse('nitrogenRecommendation.NitrogenRecommendationCreate'));
                            }}
                        >
                            {_('Pedir otra recomendación')}
                        </Button>
                    </React.Fragment>
                }
            >
                <Typography color='primary'>
                    {_(
                        'El equipo técnico de Bayer va a analizar los datos. Cerca de la fecha de refertilización te contactamos para confirmarla y enviarte la recomendación.'
                    )}
                </Typography>
            </ModalV2>
        </React.Fragment>
    );
};

export default NitrogenRecommendationForm;
